import { useEffect, useState } from 'react';
import { api, type CloseoutData } from '../api/client';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const num = (s: string) => { const n = Number(s); return Number.isFinite(n) ? n : 0; };

// Estimate-vs-actual job closeout (flywheel #3). Capture real spend after the job
// and compare to the estimate so we learn where we miss.
export function CloseoutCard({ projectId }: { projectId: string }) {
  const { t } = useLang();
  const [data, setData] = useState<CloseoutData | null>(null);
  const [open, setOpen] = useState(false);
  const [mat, setMat] = useState(''); const [lab, setLab] = useState(''); const [hrs, setHrs] = useState('');
  const [other, setOther] = useState(''); const [notes, setNotes] = useState(''); const [date, setDate] = useState('');
  const [saving, setSaving] = useState<'' | 'saving' | 'saved'>('');

  useEffect(() => {
    api.getCloseout(projectId).then((d) => {
      setData(d);
      const c = d.closeout;
      if (c) {
        setMat(c.actual_material != null ? String(c.actual_material) : '');
        setLab(c.actual_labor_cost != null ? String(c.actual_labor_cost) : '');
        setHrs(c.actual_labor_hours != null ? String(c.actual_labor_hours) : '');
        setOther(c.actual_other != null ? String(c.actual_other) : '');
        setNotes(c.notes ?? ''); setDate(c.completed_at ?? '');
        setOpen(true);
      }
    }).catch(() => {});
  }, [projectId]);

  if (!data || !data.estimated) return null;
  const est = data.estimated;
  const aMat = num(mat), aLab = num(lab), aHrs = num(hrs), aOther = num(other);
  const aDirect = aMat + aLab;                       // field-controllable direct cost
  const estDirect = est.material + est.labor;
  const estIndirect = Math.max(0, (est.total_cost || estDirect) - estDirect); // overhead/soft costs held at estimate
  const actualFullCost = aMat + aLab + aOther + estIndirect;
  const variance = (a: number, e: number) => e > 0 ? ((a - e) / e) * 100 : null;
  const vColor = (v: number | null) => v == null ? 'inherit' : v > 2 ? 'var(--danger)' : v < -2 ? 'var(--teal)' : 'inherit';
  const vText = (v: number | null) => v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
  const estMargin = est.sell > 0 ? ((est.sell - (est.total_cost || estDirect)) / est.sell) * 100 : null;
  const actMargin = est.sell > 0 && aDirect > 0 ? ((est.sell - actualFullCost) / est.sell) * 100 : null;

  async function save() {
    setSaving('saving');
    try {
      await api.saveCloseout(projectId, { actual_material: aMat, actual_labor_cost: aLab, actual_labor_hours: aHrs, actual_other: aOther, notes, completed_at: date || undefined });
      setSaving('saved'); setTimeout(() => setSaving(''), 2500);
    } catch { setSaving(''); }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>{t({ en: 'Job closeout', fil: 'Job closeout' })} <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>{t({ en: '— estimate vs actual (after the job is built)', fil: '— estimate vs actual (pagkatapos maitayo ang job)' })}</span></h2>
        {!open && <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>{t({ en: '+ Record actuals', fil: '+ I-record ang actuals' })}</button>}
      </div>
      {open && (
        <>
          <table style={{ marginTop: 8 }}>
            <thead><tr><th>{t({ en: 'Category', fil: 'Kategorya' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Estimated', fil: 'Estimated' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Actual', fil: 'Actual' })}</th><th style={{ textAlign: 'right' }}>{t({ en: 'Variance', fil: 'Variance' })}</th></tr></thead>
            <tbody>
              <tr>
                <td><strong>{t({ en: 'Material', fil: 'Material' })}</strong></td>
                <td style={{ textAlign: 'right' }}>{money(est.material)}</td>
                <td style={{ textAlign: 'right' }}><input value={mat} onChange={(e) => setMat(e.target.value)} placeholder="0" style={{ width: 110, textAlign: 'right' }} /></td>
                <td style={{ textAlign: 'right', color: vColor(variance(aMat, est.material)), fontWeight: 600 }}>{aMat ? vText(variance(aMat, est.material)) : '—'}</td>
              </tr>
              <tr>
                <td><strong>{t({ en: 'Labor $', fil: 'Labor $' })}</strong><div className="muted" style={{ fontSize: 11 }}>{t({ en: 'est.', fil: 'est.' })} {est.labor_hours ? `${est.labor_hours} hrs` : ''}</div></td>
                <td style={{ textAlign: 'right' }}>{money(est.labor)}</td>
                <td style={{ textAlign: 'right' }}><input value={lab} onChange={(e) => setLab(e.target.value)} placeholder="0" style={{ width: 110, textAlign: 'right' }} /></td>
                <td style={{ textAlign: 'right', color: vColor(variance(aLab, est.labor)), fontWeight: 600 }}>{aLab ? vText(variance(aLab, est.labor)) : '—'}</td>
              </tr>
              <tr>
                <td>{t({ en: 'Labor hours', fil: 'Labor hours' })}</td>
                <td style={{ textAlign: 'right' }}>{est.labor_hours || '—'}</td>
                <td style={{ textAlign: 'right' }}><input value={hrs} onChange={(e) => setHrs(e.target.value)} placeholder="0" style={{ width: 110, textAlign: 'right' }} /></td>
                <td style={{ textAlign: 'right', color: vColor(variance(aHrs, est.labor_hours)), fontWeight: 600 }}>{aHrs ? vText(variance(aHrs, est.labor_hours)) : '—'}</td>
              </tr>
              <tr>
                <td>{t({ en: 'Other / change orders', fil: 'Iba pa / change orders' })}</td>
                <td style={{ textAlign: 'right' }} className="muted">—</td>
                <td style={{ textAlign: 'right' }}><input value={other} onChange={(e) => setOther(e.target.value)} placeholder="0" style={{ width: 110, textAlign: 'right' }} /></td>
                <td></td>
              </tr>
              <tr style={{ borderTop: '2px solid var(--gray-200, #ddd)' }}>
                <td><strong>{t({ en: 'Direct cost', fil: 'Direct cost' })}</strong><div className="muted" style={{ fontSize: 11 }}>{t({ en: 'material + labor', fil: 'material + labor' })}</div></td>
                <td style={{ textAlign: 'right' }}><strong>{money(estDirect)}</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{aDirect ? money(aDirect) : '—'}</strong></td>
                <td style={{ textAlign: 'right', color: vColor(variance(aDirect, estDirect)), fontWeight: 700 }}>{aDirect ? vText(variance(aDirect, estDirect)) : '—'}</td>
              </tr>
            </tbody>
          </table>

          {est.sell > 0 && (
            <div style={{ display: 'flex', gap: 18, marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--gray-50, #f7f7f7)', flexWrap: 'wrap' }}>
              <div><div className="muted" style={{ fontSize: 12 }}>{t({ en: 'Sell price', fil: 'Sell price' })}</div><div style={{ fontSize: 18, fontWeight: 700 }}>{money(est.sell)}</div></div>
              <div><div className="muted" style={{ fontSize: 12 }}>{t({ en: 'Estimated margin', fil: 'Estimated margin' })}</div><div style={{ fontSize: 18, fontWeight: 700, color: 'var(--teal)' }}>{estMargin == null ? '—' : `${estMargin.toFixed(1)}%`}</div></div>
              <div><div className="muted" style={{ fontSize: 12 }}>{t({ en: 'Actual margin', fil: 'Actual margin' })}</div><div style={{ fontSize: 18, fontWeight: 700, color: actMargin == null ? 'inherit' : actMargin < (estMargin ?? 0) - 2 ? 'var(--danger)' : 'var(--teal)' }}>{actMargin == null ? t({ en: '— enter actuals', fil: '— ilagay ang actuals' }) : `${actMargin.toFixed(1)}%`}</div></div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div><label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{t({ en: 'Completed date', fil: 'Petsa ng pagkatapos' })}</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div style={{ flex: 1, minWidth: 200 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{t({ en: 'Lessons learned (optional)', fil: 'Lessons learned (opsyonal)' })}</label><input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t({ en: 'what drove the variance — for next time', fil: 'ano ang dahilan ng variance — para sa susunod' })} style={{ width: '100%' }} /></div>
            <button className="btn btn-primary" disabled={saving === 'saving'} onClick={save}>{saving === 'saved' ? `✓ ${t(G.saved)}` : saving === 'saving' ? t(G.saving) : t({ en: 'Save closeout', fil: 'I-save ang closeout' })}</button>
          </div>
        </>
      )}
    </div>
  );
}
